import {
  CLOUDINARY_TRANSFORMATION_D2C_SQUARE,
  getBlockId,
} from "@/services/contentful/assemblies/page/adapter/utils";
import {
  adaptTeaserItem,
  type TeaserItemInput,
} from "@/services/contentful/components/TeaserItem/teaser-item.adapter";
import {
  UiConfigurationAdapter,
  type UiConfigurationInput,
} from "@/services/contentful/topics/UIConfiguration/ui-configuration.adapter";
import type { SectionTeaserPageBlock } from "@/types/page-block";

const MIN_TEASER_SECTION_ITEMS = 2;
const MAX_TEASER_SECTION_ITEMS = 3;

export interface SectionTeaserInput {
  __typename: "SectionTeaser";
  sys?: { id: string };
  internalName: string;
  uiConfiguration?: UiConfigurationInput | null;
  itemsCollection?: {
    items: Array<TeaserItemInput | null>;
  } | null;
}

function getSectionId(item: SectionTeaserInput): string {
  return item.sys?.id ?? item.internalName;
}

export function SectionTeaserAdapter(
  item: SectionTeaserInput,
  index: number,
  basePath?: string
): SectionTeaserPageBlock {
  if (!Array.isArray(item.itemsCollection?.items)) {
    throw new TypeError(
      "SectionTeaserAdapter: itemsCollection.items is required"
    );
  }

  const sectionId = getSectionId(item);
  const items = item.itemsCollection.items
    .filter((teaserItem): teaserItem is TeaserItemInput => teaserItem !== null)
    .map((teaserItem, teaserIndex) =>
      adaptTeaserItem(teaserItem, {
        basePath,
        index: teaserIndex,
        parentLabel: `Teaser Section "${sectionId}"`,
        transform: CLOUDINARY_TRANSFORMATION_D2C_SQUARE,
      })
    );

  if (
    items.length < MIN_TEASER_SECTION_ITEMS ||
    items.length > MAX_TEASER_SECTION_ITEMS
  ) {
    throw new Error(
      `Teaser Section "${sectionId}" requires two or three resolved items; received ${items.length}.`
    );
  }

  const uiConfig = item.uiConfiguration
    ? UiConfigurationAdapter(item.uiConfiguration)
    : undefined;

  return {
    id: getBlockId(item, index),
    __typename: item.__typename,
    sys: item.sys,
    internalName: item.internalName,
    items,
    backgroundColor: uiConfig?.backgroundColor,
    paddingTop: uiConfig?.paddingTop,
    paddingBottom: uiConfig?.paddingBottom,
  };
}

export type { TeaserItemInput } from "@/services/contentful/components/TeaserItem/teaser-item.adapter";
